/**
 * 606 Meta5 AI Trading - Context Provider
 * Polls API every 3 seconds for live data
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  fetchAccount,
  fetchBotStatus,
  fetchPositionsOpen,
  fetchPrices,
  fetchSettings,
  fetchPerformance
} from '../lib/trading-api';

const TradingContext = createContext(null);

export const useTradingContext = () => {
  const context = useContext(TradingContext);
  if (!context) {
    throw new Error('useTradingContext must be used within TradingProvider');
  }
  return context;
};

export const TradingProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [positions, setPositions] = useState([]);
  const [prices, setPrices] = useState({});
  const [settings, setSettings] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshCore = useCallback(async () => {
    // Each fetch is independent so a single slow/failing call (e.g., MetaApi
    // rate-limit on positions) does NOT wipe out the rest of the live data.
    // The bot status used to "disappear" after a tab navigation because
    // Promise.all([fail]) would discard ALL state. We avoid that here.
    const [accountR, statusR, positionsR, pricesR] = await Promise.allSettled([
      fetchAccount(),
      fetchBotStatus(),
      fetchPositionsOpen(),
      fetchPrices(),
    ]);
    if (accountR.status === 'fulfilled') setAccount(accountR.value);
    if (statusR.status === 'fulfilled') setBotStatus(statusR.value);
    if (positionsR.status === 'fulfilled') setPositions(positionsR.value.data || []);
    if (pricesR.status === 'fulfilled') setPrices(pricesR.value);

    const firstError = [accountR, statusR, positionsR, pricesR]
      .find((r) => r.status === 'rejected');
    setError(firstError ? (firstError.reason?.message || 'fetch error') : null);
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await fetchSettings();
      setSettings(data);
    } catch (err) {
      console.error('Settings fetch error:', err);
    }
  }, []);

  const refreshPerformance = useCallback(async () => {
    try {
      const data = await fetchPerformance();
      setPerformance(data);
    } catch (err) {
      console.error('Performance fetch error:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        refreshCore(),
        refreshSettings(),
        refreshPerformance()
      ]);
      setLoading(false);
    };
    init();
  }, [refreshCore, refreshSettings, refreshPerformance]);

  // Polling every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshCore();
    }, 3000);

    return () => clearInterval(interval);
  }, [refreshCore]);

  // Refresh performance every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshPerformance();
    }, 10000);

    return () => clearInterval(interval);
  }, [refreshPerformance]);

  const value = {
    account,
    botStatus,
    positions,
    prices,
    settings,
    performance,
    loading,
    error,
    refreshCore,
    refreshSettings,
    refreshPerformance
  };

  return (
    <TradingContext.Provider value={value}>
      {children}
    </TradingContext.Provider>
  );
};

export default TradingContext;
